import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  FormControl,
  Select,
  Chip,
  Autocomplete,
  Divider,
  Alert,
  InputLabel,
} from "@mui/material";
import { useStore } from "@xyflow/react";
import { callAPI } from "../../../../functions/api";

const OUTPUT_FORMATS = [
  { value: "json", label: "JSON" },
  { value: "markdown", label: "Markdown" },
  { value: "plain_text", label: "Plain Text" },
  { value: "html", label: "HTML" },
  { value: "csv", label: "CSV" },
  { value: "xml", label: "XML" },
  { value: "yaml", label: "YAML" },
];

export const AiAgentNodeModal = ({ open, node }) => {
  const nodeData = node?.data || {};
  const [agentRoles, setAgentRoles] = useState([]);
  const [agentInstructions, setAgentInstructions] = useState([]);
  const [settings, setSettings] = useState({
    agentName: "",
    role: "",
    customRole: "",
    systemPrompt: "",
    userPrompt: "",
    outputFormat: "json",
    instructions: [],
    responseSchema: "",
    // Output parser settings (used when no external parser connected)
    parseJson: true,
    validateSchema: false,
    errorHandling: "ignore",
  });

  const nodes = useStore((s) => s.nodes);
  const edges = useStore((s) => s.edges);
  const initializedRef = useRef(false);
  const lastInitializedNodeId = useRef(null);

  // Check if external components are connected
  const externalConnections = useMemo(() => {
    if (!node?.id) return { chatModel: false, outputParser: false };

    const hasChatModel = edges.some((e) => e.target === node.id && e.targetHandle === "chatModel");
    const hasOutputParser = edges.some(
      (e) => e.target === node.id && e.targetHandle === "outputParser"
    );

    return {
      chatModel: hasChatModel,
      outputParser: hasOutputParser,
    };
  }, [edges, node?.id]);

  // Generate agent name based on input connections
  const generateAgentName = useMemo(() => {
    if (!node?.id || !nodes || !edges) return "AI Agent";

    // Find input connections to this node
    const inputEdges = edges.filter(
      (e) =>
        e.target === node.id && e.targetHandle !== "chatModel" && e.targetHandle !== "outputParser"
    );

    if (inputEdges.length === 0) return "AI Agent";

    // Get the source nodes
    const sourceNodes = inputEdges.map((edge) => {
      const sourceNode = nodes.find((n) => n.id === edge.source);
      return sourceNode?.data?.label || sourceNode?.type || "Data";
    });

    // Create a descriptive name based on input types
    const uniqueSources = [...new Set(sourceNodes)];
    if (uniqueSources.length === 1) {
      return `${uniqueSources[0]} Analysis Agent`;
    } else if (uniqueSources.length === 2) {
      return `${uniqueSources.join(" & ")} Agent`;
    } else if (uniqueSources.length > 2) {
      return `Multi-Source Analysis Agent`;
    }

    return "AI Agent";
  }, [node?.id, nodes, edges]);

  // Initialize settings from node data when modal opens (only once per node)
  useEffect(() => {
    if (open && (!initializedRef.current || lastInitializedNodeId.current !== node?.id)) {
      const existingSettings = nodeData?.output?.settings || {};

      // Auto-populate agent name if not already set
      const defaultSettings = {
        agentName: existingSettings.agentName || generateAgentName,
      };

      setSettings((prev) => ({
        ...prev,
        ...defaultSettings,
        ...existingSettings,
      }));

      initializedRef.current = true;
      lastInitializedNodeId.current = node?.id;
    }
  }, [open, node?.id, nodeData?.output?.settings]);

  // Set default role when agent roles are loaded and no role is selected
  useEffect(() => {
    if (agentRoles.length > 0 && (!settings.role || settings.role === "")) {
      setSettings((prev) => ({
        ...prev,
        role: agentRoles[0].value,
      }));
    }
  }, [agentRoles, settings.role]);

  // Fetch agent roles from API
  useEffect(() => {
    const fetchAgentRoles = async () => {
      try {
        const res = await callAPI("ai/agent/agentroles", "GET", null, true);
        if (res && Array.isArray(res)) {
          const formattedRoles = res.map((role) => ({
            value: role.name.toLowerCase().replace(/\s+/g, "_"),
            label: role.name,
            description: role.description || "",
            prompt: role.prompt || "",
          }));
          // Add custom role option
          formattedRoles.push({
            value: "custom",
            label: "Custom Role",
            description: "",
            prompt: "",
          });
          setAgentRoles(formattedRoles);
        }
      } catch (error) {
        console.error("Failed to fetch agent roles:", error);
        // Fallback to default roles
        setAgentRoles([
          { value: "general_assistant", label: "General Assistant", description: "", prompt: "" },
          { value: "custom", label: "Custom Role", description: "", prompt: "" },
        ]);
      }
    };
    fetchAgentRoles();
  }, []);

  // Fetch agent instructions from API
  useEffect(() => {
    const fetchAgentInstructions = async () => {
      try {
        const res = await callAPI("ai/agent/agentinstructions", "GET", null, true);
        if (res && Array.isArray(res)) {
          const instructionNames = res.map((instruction) => instruction.name);
          setAgentInstructions(instructionNames);
        }
      } catch (error) {
        console.error("Failed to fetch agent instructions:", error);
        // Fallback to default instructions
        setAgentInstructions([
          "Be concise and accurate",
          "Include sources when possible",
          "Format as structured data",
          "Maintain professional tone",
          "Focus on key insights",
          "Provide actionable recommendations",
          "Include confidence levels",
          "Highlight anomalies or concerns",
        ]);
      }
    };
    fetchAgentInstructions();
  }, []);

  // Update node data when settings change (debounced to prevent re-render loops)
  useEffect(() => {
    if (!node?.data?.setData || !open || !initializedRef.current) return;

    const timer = setTimeout(() => {
      node.data.setData((oldData) => ({
        ...oldData,
        output: {
          ...oldData.output,
          settings,
        },
      }));
    }, 300); // Increased delay to prevent rapid updates

    return () => clearTimeout(timer);
  }, [settings, node?.data?.setData, open]);

  const handleSettingChange = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleInstructionsChange = (event, newValue) => {
    handleSettingChange("instructions", newValue);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        height: "100%",
        overflow: "auto",
        padding: 2,
      }}
    >
      {/* Connection Status Alerts */}
      {!externalConnections.chatModel && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          ⚠ Chat model connection required. Please connect an external chat model provider.
        </Alert>
      )}

      {externalConnections.chatModel && !externalConnections.outputParser && (
        <Alert severity="info" sx={{ mb: 2 }}>
          ✔ Chat model connected. Configure output format below or connect an external output
          parser.
        </Alert>
      )}

      {externalConnections.chatModel && externalConnections.outputParser && (
        <Alert severity="success" sx={{ mb: 2 }}>
          ✔ External chat model and output parser connected. Output format settings below will be
          ignored.
        </Alert>
      )}

      {/* Agent Configuration */}
      <Box>
        <Typography sx={{ fontSize: "16px", fontWeight: "bold", mb: 2 }}>
          Agent Configuration
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {/* Agent Name */}
          <TextField
            label="Agent Name"
            size="small"
            value={settings.agentName}
            onChange={(e) => handleSettingChange("agentName", e.target.value)}
            placeholder="e.g., Data Analysis Agent"
            sx={{ backgroundColor: "#2E2E2E" }}
          />

          {/* Role Selection */}
          <FormControl size="small">
            <InputLabel>Agent Role</InputLabel>
            <Select
              value={settings.role}
              label="Agent Role"
              onChange={(e) => handleSettingChange("role", e.target.value)}
              sx={{ backgroundColor: "#2E2E2E" }}
            >
              {agentRoles.map((role) => (
                <MenuItem key={role.value} value={role.value}>
                  {role.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Custom Role Input */}
          {settings.role === "custom" && (
            <TextField
              label="Custom Role Description"
              size="small"
              value={settings.customRole}
              onChange={(e) => handleSettingChange("customRole", e.target.value)}
              placeholder="Describe the custom role..."
              multiline
              rows={2}
              sx={{ backgroundColor: "#2E2E2E" }}
            />
          )}
        </Box>
      </Box>

      <Divider />

      {/* Prompts */}
      <Box>
        <Typography sx={{ fontSize: "16px", fontWeight: "bold", mb: 2 }}>
          Prompts & Instructions
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {/* System Prompt */}
          <TextField
            label="System Prompt"
            multiline
            rows={4}
            size="small"
            value={settings.systemPrompt}
            onChange={(e) => handleSettingChange("systemPrompt", e.target.value)}
            placeholder="You are a helpful AI assistant specialized in..."
            sx={{ backgroundColor: "#2E2E2E" }}
            helperText="Defines the agent's role and behavior"
          />

          {/* User Prompt Template */}
          <TextField
            label="User Prompt Template"
            multiline
            rows={3}
            size="small"
            value={settings.userPrompt}
            onChange={(e) => handleSettingChange("userPrompt", e.target.value)}
            placeholder="Analyze the following data: {input}"
            sx={{ backgroundColor: "#2E2E2E" }}
            helperText="Use {input} as placeholder for incoming data"
          />

          {/* Common Instructions */}
          <Autocomplete
            multiple
            options={agentInstructions}
            value={settings.instructions}
            onChange={handleInstructionsChange}
            freeSolo
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Additional Instructions"
                placeholder="Add instructions..."
                size="small"
                sx={{ backgroundColor: "#2E2E2E" }}
              />
            )}
          />
        </Box>
      </Box>

      <Divider />

      {/* Output Configuration (only show if no external output parser) */}
      {!externalConnections.outputParser && (
        <Box>
          <Typography sx={{ fontSize: "16px", fontWeight: "bold", mb: 2 }}>
            Output Configuration
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {/* Output Format */}
            <FormControl size="small">
              <InputLabel>Output Format</InputLabel>
              <Select
                value={settings.outputFormat}
                label="Output Format"
                onChange={(e) => handleSettingChange("outputFormat", e.target.value)}
                sx={{ backgroundColor: "#2E2E2E" }}
              >
                {OUTPUT_FORMATS.map((format) => (
                  <MenuItem key={format.value} value={format.value}>
                    {format.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Response Schema */}
            {settings.outputFormat === "json" && (
              <TextField
                label="JSON Schema (Optional)"
                multiline
                rows={4}
                size="small"
                value={settings.responseSchema}
                onChange={(e) => handleSettingChange("responseSchema", e.target.value)}
                placeholder='{"type": "object", "properties": {...}}'
                sx={{ backgroundColor: "#2E2E2E" }}
                helperText="Define expected JSON structure"
              />
            )}
          </Box>
        </Box>
      )}

      {!externalConnections.outputParser && <Divider />}
    </Box>
  );
};
